import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { Plus, Edit3, Trash2, Tag, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  code: "",
  type: "percent",
  value: 10,
  min_subtotal: 0,
  max_discount: null,
  usage_limit: null,
  expires_at: "",
  active: true,
  description: "",
};

export default function AdminCoupons() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // null | {coupon, isNew}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/coupons");
      setList(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => setDrawer({ coupon: { ...EMPTY }, isNew: true });
  const openEdit = (c) => setDrawer({ coupon: { ...EMPTY, ...c }, isNew: false });
  const close = () => setDrawer(null);

  const save = async (e) => {
    e.preventDefault();
    const c = drawer.coupon;
    setSaving(true);
    try {
      const payload = {
        code: c.code.toUpperCase(),
        type: c.type,
        value: Number(c.value),
        min_subtotal: Number(c.min_subtotal) || 0,
        max_discount: c.max_discount === "" || c.max_discount == null ? null : Number(c.max_discount),
        usage_limit: c.usage_limit === "" || c.usage_limit == null ? null : Number(c.usage_limit),
        expires_at: c.expires_at || null,
        active: !!c.active,
        description: c.description || "",
      };
      if (drawer.isNew) {
        await api.post("/admin/coupons", payload);
        toast.success("Coupon created");
      } else {
        await api.put(`/admin/coupons/${c.id}`, payload);
        toast.success("Coupon updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const update = (k, v) => setDrawer((d) => ({ ...d, coupon: { ...d.coupon, [k]: v } }));

  return (
    <div className="p-6 md:p-10" data-testid="admin-coupons">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="font-script text-3xl text-blush-400">discount engine</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Coupons</h1>
        </div>
        <button onClick={openNew} className="btn-maroon inline-flex items-center gap-2" data-testid="coupon-new-btn">
          <Plus size={14} strokeWidth={1.6} /> New coupon
        </button>
      </header>

      {loading ? (
        <div className="p-10 text-center text-ink-500 font-body">Loading…</div>
      ) : list.length === 0 ? (
        <div className="p-12 text-center bg-white border border-blush-100 rounded-3xl">
          <Tag size={32} strokeWidth={1.2} className="mx-auto text-blush-400 mb-3" />
          <p className="font-heading text-xl text-ink-900">No coupons yet</p>
          <p className="mt-1 font-body text-ink-500 text-sm">Create your first promo code.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="rounded-3xl bg-white border border-blush-100 p-5" data-testid={`coupon-row-${c.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading text-2xl text-ink-900 tracking-wider">{c.code}</p>
                  <p className="text-xs font-body text-ink-500 mt-1">
                    {c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}
                    {c.min_subtotal > 0 && ` · Min ₹${c.min_subtotal}`}
                  </p>
                  {c.description && <p className="text-xs font-body text-ink-700 mt-2">{c.description}</p>}
                </div>
                <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full font-body ${c.active ? "bg-blush-50 text-blush-600" : "bg-ink-100 text-ink-500"}`}>
                  {c.active ? "Active" : "Off"}
                </span>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs font-body text-ink-500">
                <span>Used {c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</span>
                {c.expires_at && <span>Expires {(c.expires_at || "").slice(0, 10)}</span>}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button onClick={() => openEdit(c)} className="btn-ghost-rose text-xs inline-flex items-center gap-1" data-testid={`coupon-edit-${c.code}`}>
                  <Edit3 size={12} strokeWidth={1.6} /> Edit
                </button>
                <button onClick={() => del(c)} className="h-8 w-8 grid place-items-center rounded-full text-blush-600 hover:bg-blush-50" aria-label="Delete" data-testid={`coupon-delete-${c.code}`}>
                  <Trash2 size={14} strokeWidth={1.6} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawer && (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm" onClick={close}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-y-0 right-0 w-full sm:max-w-lg bg-cream-50 overflow-y-auto"
            data-testid="coupon-drawer"
          >
            <div className="sticky top-0 bg-cream-50/95 backdrop-blur border-b border-blush-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-heading text-2xl text-ink-900">{drawer.isNew ? "New coupon" : "Edit coupon"}</h3>
              <button type="button" onClick={close} className="h-9 w-9 grid place-items-center rounded-full hover:bg-blush-50" aria-label="Close">
                <X size={18} strokeWidth={1.6} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <Field label="Code" required>
                <input
                  required
                  value={drawer.coupon.code}
                  onChange={(e) => update("code", e.target.value.toUpperCase())}
                  className="field uppercase tracking-wider"
                  data-testid="coupon-field-code"
                />
              </Field>
              <Field label="Description">
                <input value={drawer.coupon.description} onChange={(e) => update("description", e.target.value)} className="field" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type">
                  <select value={drawer.coupon.type} onChange={(e) => update("type", e.target.value)} className="field" data-testid="coupon-field-type">
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat (₹)</option>
                  </select>
                </Field>
                <Field label={drawer.coupon.type === "percent" ? "Value (%)" : "Value (₹)"}>
                  <input type="number" min="1" value={drawer.coupon.value} onChange={(e) => update("value", e.target.value)} className="field" required data-testid="coupon-field-value" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Min subtotal (₹)">
                  <input type="number" min="0" value={drawer.coupon.min_subtotal} onChange={(e) => update("min_subtotal", e.target.value)} className="field" />
                </Field>
                {drawer.coupon.type === "percent" && (
                  <Field label="Max discount cap (₹)">
                    <input type="number" min="0" value={drawer.coupon.max_discount ?? ""} onChange={(e) => update("max_discount", e.target.value)} className="field" placeholder="optional" />
                  </Field>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Usage limit">
                  <input type="number" min="1" value={drawer.coupon.usage_limit ?? ""} onChange={(e) => update("usage_limit", e.target.value)} className="field" placeholder="optional" />
                </Field>
                <Field label="Expires on">
                  <input type="date" value={(drawer.coupon.expires_at || "").slice(0, 10)} onChange={(e) => update("expires_at", e.target.value)} className="field" />
                </Field>
              </div>
              <label className="flex items-center gap-2 font-body text-sm text-ink-700 cursor-pointer">
                <input type="checkbox" checked={!!drawer.coupon.active} onChange={(e) => update("active", e.target.checked)} />
                Active
              </label>
              <div className="pt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={close} className="btn-ghost-rose text-xs">Cancel</button>
                <button type="submit" disabled={saving} className="btn-maroon text-xs" data-testid="coupon-save-btn">
                  {saving ? "Saving…" : drawer.isNew ? "Create" : "Save changes"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-body text-ink-500 mb-2">
        {label}{required && <span className="text-blush-600"> *</span>}
      </span>
      {children}
    </label>
  );
}
